// Shared visual building blocks for the manager dashboard and approval queue.
// Centralising these keeps both surfaces consistent: same leave-type colour
// coding, same coverage ring, same decision card.

import React from 'react';
import { Box, Button, Chip, Stack, Tooltip, Typography } from '@mui/material';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VisibilityRoundedIcon from '@mui/icons-material/VisibilityRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import CalendarMonthRoundedIcon from '@mui/icons-material/CalendarMonthRounded';
import AttachFileRoundedIcon from '@mui/icons-material/AttachFileRounded';
import { alpha, useTheme } from '@mui/material/styles';

import { StatusBadge } from '../ui';
import { formatDateOnly } from '../../utils/notificationUtils';
import {
  durationInDays,
  getEmployeeName,
  getLeaveTypeLabel,
  getLeaveTypeVisual,
  normalizeLeaveStatus,
} from '../../utils/leaveVisuals';

function toneColor(theme, tone) {
  return theme.palette?.[tone]?.main || theme.palette.primary.main;
}

export function LeaveTypeBadge({ leaveType, size = 42 }) {
  const theme = useTheme();
  const { tone, Icon } = getLeaveTypeVisual(leaveType);
  const accent = toneColor(theme, tone);
  return (
    <Box
      aria-hidden
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: '11px',
        display: 'grid',
        placeItems: 'center',
        color: accent,
        backgroundColor: alpha(accent, theme.palette.mode === 'dark' ? 0.22 : 0.12),
      }}
    >
      <Icon sx={{ fontSize: size * 0.5 }} />
    </Box>
  );
}

export function CoverageRing({
  value = 0,
  size = 104,
  stroke = 9,
  color,
  trackColor,
  primaryLabel,
  secondaryLabel,
}) {
  const theme = useTheme();
  const pct = Math.max(0, Math.min(100, Number(value) || 0));
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (pct / 100) * circumference;
  const ringColor = color || theme.palette.success.main;
  const ring = trackColor || alpha(theme.palette.text.primary, 0.12);

  return (
    <Box sx={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} role="img" aria-label={`${Math.round(pct)} percent team coverage`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={ring} strokeWidth={stroke} />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={ringColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={`${dash} ${circumference - dash}`}
          transform={`rotate(-90 ${size / 2} ${size / 2})`}
        />
      </svg>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography sx={{ fontWeight: 900, lineHeight: 1, color: 'inherit', fontSize: size * 0.2 }}>
          {primaryLabel ?? `${Math.round(pct)}%`}
        </Typography>
        {secondaryLabel ? (
          <Typography sx={{ fontSize: size * 0.095, opacity: 0.82, color: 'inherit', mt: 0.3 }}>
            {secondaryLabel}
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
}

export function ManagerKpiTile({ tone = 'primary', icon, label, value, hint, onClick }) {
  const theme = useTheme();
  const accent = toneColor(theme, tone);
  const dark = theme.palette.mode === 'dark';
  const textColor = dark ? theme.palette[tone]?.light || accent : theme.palette[tone]?.dark || accent;

  return (
    <Box
      component={onClick ? 'button' : 'div'}
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      sx={{
        width: '100%',
        textAlign: 'left',
        font: 'inherit',
        cursor: onClick ? 'pointer' : 'default',
        p: 1.05,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: alpha(accent, 0.22),
        backgroundColor: alpha(accent, dark ? 0.16 : 0.09),
        display: 'flex',
        flexDirection: 'column',
        gap: 0.6,
        transition: theme.transitions.create(['border-color', 'background-color'], {
          duration: theme.transitions.duration.shortest,
        }),
        '&:hover': onClick ? { borderColor: alpha(accent, 0.42), backgroundColor: alpha(accent, dark ? 0.22 : 0.14) } : undefined,
      }}
    >
      <Box
        aria-hidden
        sx={{ width: 30, height: 30, borderRadius: '9px', display: 'grid', placeItems: 'center', color: accent, backgroundColor: alpha(accent, dark ? 0.24 : 0.16) }}
      >
        {icon}
      </Box>
      <Typography variant="caption" sx={{ fontWeight: 800, color: textColor }}>
        {label}
      </Typography>
      <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1, color: textColor }}>
        {value}
      </Typography>
      {hint ? (
        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
          {hint}
        </Typography>
      ) : null}
    </Box>
  );
}

// Horizontal bar chart of confirmed team availability across the working week.
export function TeamWeekStrip({ week = [], teamSize = 0 }) {
  const theme = useTheme();
  const max = Math.max(teamSize, 1);
  const tightest = week.find((day) => day.isTightest);

  return (
    <Box>
      <Stack direction="row" spacing={0.6} alignItems="flex-end" sx={{ height: 96 }}>
        {week.map((day) => {
          const ratio = Math.max(0.12, day.available / max);
          const barColor = day.isTightest ? theme.palette.warning.main : theme.palette.primary.main;
          return (
            <Stack key={day.key} spacing={0.6} alignItems="center" sx={{ flex: 1, minWidth: 0 }}>
              <Box sx={{ width: '100%', height: 72, display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}>
                <Tooltip title={`${day.available} of ${teamSize} available · ${day.off} off`} arrow>
                  <Box
                    sx={{
                      width: '62%',
                      height: `${ratio * 100}%`,
                      borderRadius: '7px 7px 0 0',
                      backgroundColor: barColor,
                      display: 'flex',
                      alignItems: 'flex-start',
                      justifyContent: 'center',
                      pt: 0.4,
                    }}
                  >
                    <Typography sx={{ fontSize: 12, fontWeight: 800, color: theme.palette.getContrastText(barColor) }}>
                      {day.available}
                    </Typography>
                  </Box>
                </Tooltip>
              </Box>
              <Typography
                variant="caption"
                sx={{ color: day.isTightest ? 'warning.main' : 'text.secondary', fontWeight: day.isTightest ? 800 : 600 }}
              >
                {day.label}
              </Typography>
            </Stack>
          );
        })}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        {tightest
          ? `${tightest.label} is tightest — ${tightest.available} of ${teamSize} in (${tightest.off} off).`
          : `Full coverage this week — all ${teamSize} in.`}
      </Typography>
    </Box>
  );
}

// One reviewable request: leave-type colour, dates, duration, overlap warning,
// and (when pending + handlers supplied) inline approve / decline / view.
export function RequestReviewCard({ request, overlaps = [], onApprove, onReject, onView, busy = false }) {
  const theme = useTheme();
  const status = normalizeLeaveStatus(request?.status);
  const isPending = status === 'pending';
  const duration = durationInDays(request);
  const start = formatDateOnly(request?.start_date || request?.date_from) || '-';
  const end = formatDateOnly(request?.end_date || request?.date_to) || start;
  const hasAttachment = Boolean(request?.attachment);
  const canAct = isPending && (onApprove || onReject);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.25,
        p: 1.1,
        borderRadius: '12px',
        border: '1px solid',
        borderColor: theme.hr?.line?.soft || 'divider',
        backgroundColor:
          theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.7)
            : alpha(theme.palette.background.paper, 0.96),
        opacity: busy ? 0.6 : 1,
        transition: theme.transitions.create('opacity', { duration: theme.transitions.duration.shortest }),
      }}
    >
      <LeaveTypeBadge leaveType={request?.leave_type} />
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 850 }} noWrap>
              {getEmployeeName(request)}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap>
              {[request?.user?.position || request?.position, getLeaveTypeLabel(request?.leave_type)].filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <StatusBadge status={status} />
        </Stack>

        <Stack direction="row" spacing={0.7} useFlexGap flexWrap="wrap" sx={{ mt: 0.9 }}>
          <Chip
            size="small"
            variant="outlined"
            icon={<CalendarMonthRoundedIcon sx={{ fontSize: 15 }} />}
            label={start === end ? start : `${start} – ${end}`}
            sx={{ borderRadius: '8px', '& .MuiChip-label': { fontWeight: 700 } }}
          />
          {duration ? (
            <Chip size="small" label={`${duration} day${duration > 1 ? 's' : ''}`} sx={{ borderRadius: '8px', '& .MuiChip-label': { fontWeight: 700 } }} />
          ) : null}
          {hasAttachment ? (
            <Chip
              size="small"
              variant="outlined"
              icon={<AttachFileRoundedIcon sx={{ fontSize: 15 }} />}
              label="Attachment"
              sx={{ borderRadius: '8px' }}
            />
          ) : null}
        </Stack>

        {overlaps.length ? (
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ mt: 0.9, color: 'warning.main' }}>
            <WarningAmberRoundedIcon sx={{ fontSize: 16 }} />
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              {overlaps.length === 1
                ? `Overlaps with ${overlaps[0].name}`
                : `Overlaps with ${overlaps[0].name} +${overlaps.length - 1} more`}
            </Typography>
          </Stack>
        ) : null}

        {(canAct || onView) ? (
          <Stack direction="row" spacing={0.8} sx={{ mt: 1.1 }} useFlexGap flexWrap="wrap">
            {canAct ? (
              <>
                <Button
                  size="small"
                  variant="outlined"
                  color="success"
                  startIcon={<CheckRoundedIcon />}
                  disabled={busy}
                  onClick={() => onApprove?.(request)}
                >
                  Approve
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<CloseRoundedIcon />}
                  disabled={busy}
                  onClick={() => onReject?.(request)}
                >
                  Decline
                </Button>
              </>
            ) : null}
            {onView ? (
              <Button size="small" startIcon={<VisibilityRoundedIcon />} sx={{ ml: 'auto' }} onClick={() => onView(request)}>
                View
              </Button>
            ) : null}
          </Stack>
        ) : null}
      </Box>
    </Box>
  );
}
